import React, { useState, useEffect } from 'react';
import { AlertTriangle, Trash2, X, ShieldAlert, CheckCircle, RotateCcw } from 'lucide-react';

export default function ClearStackModal({
  isOpen,
  onClose,
  onConfirm,
  protocolCount = 0,
  isClearing = false
}) {
  const [confirmInput, setConfirmInput] = useState('');

  // Reset input when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setConfirmInput('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isConfirmed = confirmInput.trim().toUpperCase() === 'CLEAR';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isConfirmed && !isClearing) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div 
        className="bg-slate-900 border border-red-500/30 w-full max-w-md rounded-2xl shadow-2xl p-6 relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Ambient warning background light */}
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-red-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={isClearing}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition disabled:opacity-50"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 rounded-2xl bg-red-500/10 text-red-400 border border-red-500/20">
            <Trash2 className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white tracking-tight">
              Clear All Protocols
            </h3>
            <p className="text-xs text-red-400 font-medium">
              Destructive Stack Action
            </p>
          </div>
        </div>

        {/* Warning Body */}
        <div className="space-y-3.5 text-xs text-slate-300">
          <p className="leading-relaxed">
            Are you sure you want to delete all <strong className="text-white font-mono">{protocolCount} protocols</strong> from your current stack?
          </p>

          <div className="p-3 rounded-xl bg-slate-950/80 border border-sky-500/20 text-slate-300 space-y-1.5">
            <div className="flex items-center space-x-2 text-sky-400 font-semibold text-xs">
              <RotateCcw className="w-4 h-4 flex-shrink-0" />
              <span>Automatic Recovery Checkpoint</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Don't worry: your current stack will be saved to an automatic recovery checkpoint before deletion. You can restore your protocols at any time from the <strong className="text-white">Checkpoints</strong> menu.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 pt-1">
            <div>
              <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                Type <span className="text-red-400 font-mono">CLEAR</span> to confirm:
              </label>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="CLEAR"
                disabled={isClearing}
                autoFocus
                className="w-full bg-slate-950 border border-slate-800 focus:border-red-500 rounded-xl px-3.5 py-2 text-sm text-white font-mono placeholder-slate-600 focus:outline-none transition uppercase"
              />
            </div>

            <div className="flex items-center justify-end space-x-3 pt-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isClearing}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold border border-slate-700 transition disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={!isConfirmed || isClearing}
                className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition shadow-lg shadow-red-600/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isClearing ? 'Clearing Stack...' : 'Confirm & Clear Stack'}</span>
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
}
